import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

export default function PlatformUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'create' | user object for edit
  const [form, setForm] = useState({ username: '', email: '', password: '', full_name: '', phone: '', role: 'viewer', status: 'active' });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data.data || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openCreate = () => { setForm({ username: '', email: '', password: '', full_name: '', phone: '', role: 'viewer', status: 'active' }); setModal('create'); };
  const openEdit = (u) => { setForm({ username: u.username, email: u.email, password: '', full_name: u.full_name || '', phone: u.phone || '', role: u.role, status: u.status || 'active' }); setModal(u); };

  const handleSave = async () => {
    if (!form.username || !form.email) return;
    if (modal === 'create' && !form.full_name) return;
    setSaving(true);
    try {
      if (modal === 'create') {
        if (!form.password) { setSaving(false); return; }
        await api.post('/users', form);
      } else {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/users/${modal.id}`, payload);
      }
      setModal(null);
      fetchUsers();
    } catch {} finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this platform user?')) return;
    setDeleting(id);
    try { await api.delete(`/users/${id}`); fetchUsers(); } catch {} finally { setDeleting(null); }
  };

  const roleColors = { superadmin: 'bg-red-100 text-red-700', admin: 'bg-purple-100 text-purple-700', viewer: 'bg-gray-100 text-gray-600' };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Platform Users</h2>
          <p className="text-sm text-gray-500 mt-0.5">Manage superadmin and platform administrator accounts</p>
        </div>
        <button onClick={openCreate} className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          <span>Add User</span>
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
          <div className="text-xs text-gray-500 uppercase font-medium">Total Users</div>
          <div className="text-2xl font-bold text-gray-900 mt-1">{users.length}</div>
        </div>
        <div className="bg-green-50 rounded-xl border border-green-100 p-4">
          <div className="text-xs text-green-600 uppercase font-medium">Active</div>
          <div className="text-2xl font-bold text-green-700 mt-1">{users.filter(u => u.status === 'active').length}</div>
        </div>
        <div className="bg-red-50 rounded-xl border border-red-100 p-4">
          <div className="text-xs text-red-600 uppercase font-medium">Superadmins</div>
          <div className="text-2xl font-bold text-red-700 mt-1">{users.filter(u => u.role === 'superadmin').length}</div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase">
              {['User', 'Role', 'Status', 'Last Login', 'Created', 'Actions'].map(h => <th key={h} className="px-4 py-3 text-left">{h}</th>)}
            </tr></thead>
            <tbody className="divide-y divide-gray-50">
              {users.map(u => (
                <tr key={u.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center text-white text-xs font-bold">{u.username?.[0]?.toUpperCase()}</div>
                      <div><div className="text-sm font-medium text-gray-900">{u.username}</div><div className="text-xs text-gray-400">{u.email}</div></div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleColors[u.role] || 'bg-gray-100 text-gray-600'}`}>{u.role}</span></td>
                  <td className="px-4 py-3">{u.status === 'active' ? <span className="inline-flex items-center text-xs text-green-600 font-medium"><span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1.5" />Active</span> : <span className="inline-flex items-center text-xs text-gray-400 font-medium"><span className="w-1.5 h-1.5 bg-gray-300 rounded-full mr-1.5" />{u.status}</span>}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{u.last_login ? new Date(u.last_login).toLocaleString() : '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <button onClick={() => openEdit(u)} className="text-xs text-blue-600 hover:text-blue-700 font-medium px-2 py-1 rounded hover:bg-blue-50">Edit</button>
                      <button onClick={() => handleDelete(u.id)} disabled={deleting === u.id} className="text-xs text-red-500 hover:text-red-600 font-medium px-2 py-1 rounded hover:bg-red-50 disabled:opacity-50">{deleting === u.id ? '...' : 'Delete'}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"><div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl">
          <h3 className="text-lg font-bold text-gray-900">{modal === 'create' ? 'Add Platform User' : 'Edit User'}</h3>
          <div className="space-y-3 mt-4">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Username *</label><input value={form.username} onChange={e => setForm(p => ({ ...p, username: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" disabled={modal !== 'create'} /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Full Name *</label><input value={form.full_name} onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Full name" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Email *</label><input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Phone</label><input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" placeholder="Phone number" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Password {modal === 'create' ? '*' : '(leave blank to keep)'}</label><input type="password" value={form.password} onChange={e => setForm(p => ({ ...p, password: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" /></div>
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
              <select value={form.role} onChange={e => setForm(p => ({ ...p, role: e.target.value }))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
                <option value="superadmin">Superadmin</option><option value="admin">Platform Admin</option><option value="viewer">Viewer</option>
              </select></div>
            <div className="flex items-center space-x-2">
              <div className="w-full"><label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
                <option value="active">Active</option><option value="inactive">Inactive</option><option value="locked">Locked</option>
              </select></div>
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-5">
            <button onClick={() => setModal(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">{saving ? 'Saving...' : modal === 'create' ? 'Create' : 'Update'}</button>
          </div>
        </div></div>
      )}
    </div>
  );
}
